/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import { useMutation } from '@apollo/client';

import { InlineNotification, MuiFade } from '@coursera/cds-core';
import { useTracker } from '@coursera/event-pulse/react';

import { CoachButton } from 'bundles/ai-coach-platform/components/building-blocks';
import { useCoachChatContext } from 'bundles/ai-coach-platform/components/patterns/chat/context/CoachChatContext';
import type {
  AiCoachPlatform_SaveCoachUserSettingsMutation as SaveCoachUserSettingsMutation,
  AiCoachPlatform_SaveCoachUserSettingsMutationVariables as SaveCoachUserSettingsMutationVariables,
} from 'bundles/ai-coach-platform/queries/__generated__/aiCoachSaveUserSettingsMutation';
import aiCoachSaveUserSettingsMutation from 'bundles/ai-coach-platform/queries/aiCoachSaveUserSettingsMutation.graphql';
import { getFeatureNameLabels } from 'bundles/ai-coach-platform/utils/brandLabels';
import localStorageEx from 'bundles/common/utils/localStorageEx';

import _t from 'i18n!nls/ai-coach-platform';

export const COACH_CHAT_MEMORY_NOTIFICATION_DISMISSED = 'AiCoachPlatform_NewMemoryFeatureNotificationDismissed';
export const COACH_CHAT_MEMORY_NOTIFICATION_SHOWN = 'AiCoachPlatform_NewMemoryFeatureNotificationShown';

export const isNotificationDismissed = (): boolean | null => {
  return localStorageEx.getItem<boolean>(COACH_CHAT_MEMORY_NOTIFICATION_DISMISSED, (value) => value === 'true', false);
};

const isNotificationShown = (): boolean | null => {
  return localStorageEx.getItem<boolean>(COACH_CHAT_MEMORY_NOTIFICATION_SHOWN, (value) => value === 'true', false);
};

const setNotificationDismissed = () => {
  localStorageEx.setItem(COACH_CHAT_MEMORY_NOTIFICATION_DISMISSED, true, () => 'true');
};

const setNotificationShown = () => {
  localStorageEx.setItem(COACH_CHAT_MEMORY_NOTIFICATION_SHOWN, true, () => 'true');
};

const styles = {
  notificationContainer: css`
    margin: 0 var(--cds-spacing-200) var(--cds-spacing-200);

    > div {
      box-shadow: 0 6px 24px 0 var(--cds-color-darken-100);
    }
  `,
};

type Props = {
  onDismiss: () => void;
  title?: string;
  content?: string;
  brandLabel?: string;
};

export const MemoryPreferenceNotification = (props: Props): React.ReactElement => {
  const { onDismiss, title, content, brandLabel = 'Coach' } = props;
  const { isBrandUpdateEnabled } = useCoachChatContext();
  const { featureNameLabel } = getFeatureNameLabels(isBrandUpdateEnabled, brandLabel);
  const [saveSettings] = useMutation<SaveCoachUserSettingsMutation, SaveCoachUserSettingsMutationVariables>(
    aiCoachSaveUserSettingsMutation,
    {
      context: { clientName: 'gatewayGql' },
    }
  );
  const track = useTracker();
  const [showNotification, setShowNotification] = React.useState<boolean>(false);

  const defaultTitle = _t('New! #{featureNameLabel} just got more helpful', {
    featureNameLabel,
  });

  const defaultContent = _t(
    `#{featureNameLabel} can now remember things you've shared, like your goals, interests, or learning preferences, to personalize your experience over time. Go to settings > Memory to learn more.`,
    {
      featureNameLabel,
    }
  );

  const notificationTitle = title ?? defaultTitle;
  const notificationContent = content ?? defaultContent;

  const handleEventing = (enabled = false) => {
    track('interact_coach_settings', {
      settingsInteractionType: `toggle_memory_${enabled ? 'on' : 'off'}`,
    });
  };

  const handleOptIn = () => {
    if (isNotificationShown()) {
      // optimization: skip API call if we already showed the notification before,
      // just update the show state
      setShowNotification(true);
    } else {
      handleEventing(true);
      saveSettings({
        variables: {
          input: {
            memoryPreferenceInput: {
              enableMemory: true,
            },
          },
        },
      }).then(() => {
        // only show the notification if the API call succeeds
        // and set in localStorage to prevent API call again for next time
        setShowNotification(true);
        setNotificationShown();
      });
    }
  };

  const handleOptOut = () => {
    setShowNotification(false);
    onDismiss();
    handleEventing(false);
    saveSettings({
      variables: {
        input: {
          memoryPreferenceInput: {
            enableMemory: false,
          },
        },
      },
    }).then(() => {
      setNotificationDismissed();
    });
  };

  const handleDismiss = () => {
    setShowNotification(false);
    setNotificationDismissed();
    onDismiss();
  };

  // effect to run on mount only
  React.useEffect(() => {
    // slight delay to show this after Coach UI is loaded
    setTimeout(() => {
      handleOptIn();
    }, 500);
  }, []); // eslint-disable-line

  return (
    <div css={styles.notificationContainer}>
      <MuiFade in={showNotification} timeout={300}>
        <InlineNotification
          actions={
            <div
              css={css`
                display: flex;
              `}
            >
              <CoachButton size="small" onClick={handleDismiss}>
                {_t('Sounds great')}
              </CoachButton>
              <CoachButton variant="ghost" size="small" onClick={handleOptOut}>
                {_t('Turn it off')}
              </CoachButton>
            </div>
          }
          severity="information"
          title={notificationTitle}
          titleComponent="h2"
        >
          {notificationContent}
        </InlineNotification>
      </MuiFade>
    </div>
  );
};

export default MemoryPreferenceNotification;
